import { Component, ElementRef, HostListener, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { SharedService } from 'app/Shared/Services/shared.service';
import { UserInventory } from 'app/Users/models';
import { Plan } from '../models';
import { Store } from '@ngrx/store';
import { AppState } from 'app/store/model';
import { combineLatest, first, Subscription } from 'rxjs';
import { UserActions } from 'app/Users/Services/actions';
import { CameraActions } from '../Services/actions';
import { environment } from 'environments/environment';


@Component({
    selector: 'app-camera-detections-stock',
    templateUrl: './camera-detections-stock.component.html',
    styleUrls: ['./camera-detections-stock.component.scss'],
    standalone: false
})
export class CameraDetectionsStockComponent implements OnInit, OnDestroy {
    readonly userInventoryResult$ = this.store.select((state: AppState) => state.Users.userInventory);
    readonly planResult$ = this.store.select((state: AppState) => state.camera.plans);

    userInventorySub: Subscription;
    planSub: Subscription;
    inventory: UserInventory;
    plans: Plan[];
    inventoryLoading: boolean;
    plansLoading: boolean;

    cameraList: {
        alias: string;
        available: number;
        used: number;
        days_storage: number;
        id: number;
    }[];

    filterList: {
        resolution: string;
        plans: {
            id: number;
            days_storage: number;
        }[];
    }[] = [];

    possibleSelections: {
        id: number;
        selected: boolean;
        days_storage: number;
    }[] = [];

    filter: number[] = [];

    tableColumns = ['plan', 'total', 'using', 'graph', 'available'];

    isMaster = localStorage.getItem('profile_name') === '7d4e9f24-17e8-11ec-9621-0242ac130002';
    isMasterAssociate = localStorage.getItem('profile_name') === '7415bb4a-c1e9-401d-870a-7af65a8e3acd';

    constructor(private sharedService: SharedService, private element: ElementRef, private readonly store: Store<AppState>) { }

    ngOnInit(): void {
        localStorage.setItem('activeTab', 'info');
        this.inventoryLoading = true;
        this.plansLoading = true;

        this.store.dispatch(UserActions.get_user_inventory({
            user_sub: localStorage.getItem('sub'),
            child_sub: localStorage.getItem('clientView')
        }));

        this.store.dispatch(CameraActions.get_plans({
            associateId: environment.associateId
        }));

        this.subscribeStores();
    }

    ngOnDestroy() {
        if (this.userInventorySub) {
            this.userInventorySub.unsubscribe();
        }
        if (this.planSub) {
            this.planSub.unsubscribe();
        }

        this.store.dispatch(CameraActions.cleanup());
        this.store.dispatch(UserActions.cleanup());
    }

    subscribeStores() {
        combineLatest([this.userInventoryResult$, this.planResult$]).pipe(
            first(([inventoryResult, planResult]) => inventoryResult !== null && planResult !== null)
        ).subscribe(([inventoryResult, planResult]) => {
            this.inventory = inventoryResult[0];
            this.plans = planResult;
            this.inventoryLoading = false;
            this.plansLoading = false;
            if (this.inventory && this.plans) {
                this.makeList();
                this.loadFilter();
            }
        });
    }

    makeList() {
        if (this.inventory && this.plans) {
            this.cameraList = [];

            this.plans.forEach(plan => {
                const cameras = this.inventory.inventory.ci;
                if (cameras[plan.id]) {
                    this.cameraList.push({
                        alias: plan.resolution.alias,
                        available: cameras[plan.id].available,
                        used: cameras[plan.id].used,
                        days_storage: plan.days_storage,
                        id: plan.id
                    });
                }
            });
        }
    }

    loadFilter() {
        this.filterList = [];
        const possiblePlans = this.cameraList.map(element => element.id);
        this.plans.forEach((plan) => {
            if (possiblePlans.includes(plan.id)) {
                const resolution = plan.resolution.alias;
                const index = this.filterList.findIndex((element) => element.resolution === resolution);
                if (index >= 0) {
                    this.filterList[index].plans.push({
                        id: plan.id,
                        days_storage: plan.days_storage
                    });
                } else {
                    this.filterList.push({
                        resolution: resolution,
                        plans: [{
                            id: plan.id,
                            days_storage: plan.days_storage
                        }]
                    });
                }
            }
        });
    }

    toggleSelection(category, event) {
        const ul = document.querySelector('.select');
        this.possibleSelections = [];
        if (ul?.classList.contains('open') && document.querySelector('li.active').id === category.resolution) {
            ul?.classList.remove('open');
            if (document.querySelector('li.active')) {
                document.querySelector('li.active').classList.remove('active');
            }
            this.possibleSelections = [];
        } else {
            ul?.classList.remove('open');
            if (document.querySelector('li.active')) {
                document.querySelector('li.active').classList.remove('active');
            }
            ul?.classList.add('open');
            event.currentTarget.classList.add('active');

            this.possibleSelections = category.plans.map(plan => {
                return {
                    id: plan.id,
                    days_storage: plan.days_storage,
                    selected: this.filter.includes(plan.id)
                };
            });
        }
    }

    onFilterClick(): void {
        if (this.cameraList?.length > 0) {
            this.toggleDropdown();
        }
    }

    toggleFilter(plan, event) {
        const li = event.currentTarget;
        const index = this.filter.indexOf(plan.id);
        if (index >= 0) {
            li.classList.remove('selected');
            this.filter.splice(index, 1);
        } else {
            li.classList.add('selected');
            this.filter.push(plan.id);
        }

        this.makeList();
        if (this.filter.length > 0) {
            this.populateFilter();
        }
    }

    populateFilter() {
        this.cameraList = this.cameraList.filter(element => {
            return this.filter.includes(element.id);
        });
    }

    clearFilter() {
        this.filter = [];
        this.makeList();
        this.toggleDropdown(true);
    }

    toggleDropdown(forceClose = false) {
        const dropdown = document.querySelector('.open-filter');

        if (dropdown?.classList.contains('active') || forceClose) {
            dropdown?.classList.remove('active');
            const ul = document.querySelector('.select');
            ul.classList.remove('open');
            this.possibleSelections = [];
            document.querySelector('li.active')?.classList.remove('active');
            document.querySelector('.filter')?.classList.remove('active');
        } else {
            dropdown?.classList.add('active');
            document.querySelector('.filter')?.classList.add('active');
        }
    }

    @HostListener('document:click', ['$event'])
    clickOutside(event: Event) {
        if (!this.element.nativeElement.contains(event.target)) {
            this.toggleDropdown(true);
        }
    }

    openUpdateModal() {
        const used = Number(this.inventory?.inventory?.ai?.used) || 0;
        const available = Number(this.inventory?.inventory?.ai?.available) || 0;

        const dataModal = {
            data: {
                inventory_info: this.inventory,
                plans: this.plans,
                env: 'detections-stock',
                analyticUsed: used,
                analyticAvailable: available,
                analyticTotal: used + available,
                analyticMin: used
            },
            modalHeight: '90vh',
            modalWidth: '800px',
            backdropClass: 'lightBackdrop',
            tipoModal: 'deletar_kit_parceiro'
        };
        this.sharedService.returnDataToModal(dataModal);
    }

}
